import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-hot-toast';

const AdminQuizAttempts = () => {
  const { quizId } = useParams();
  const [attempts, setAttempts] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [quizId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [quizData, attemptsData] = await Promise.all([
        adminAPI.getQuiz(quizId),
        adminAPI.getQuizAttempts(quizId)
      ]);
      
      // Handle quiz data
      const quizInfo = quizData.data || quizData;
      setQuiz(quizInfo);
      
      // Handle attempts data
      let attemptsList = [];
      if (Array.isArray(attemptsData)) {
        attemptsList = attemptsData;
      } else if (attemptsData.data && Array.isArray(attemptsData.data)) {
        attemptsList = attemptsData.data;
      } else if (attemptsData.attempts && Array.isArray(attemptsData.attempts)) {
        attemptsList = attemptsData.attempts;
      } else {
        attemptsList = [];
      }
      
      setAttempts(attemptsList);
    } catch (error) {
      toast.error('Failed to load attempts');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // CSV Export Function
  const exportToCSV = () => {
    if (!attempts || attempts.length === 0) {
      toast.error('No data to export');
      return;
    }

    setExportLoading(true);
    
    try {
      // Prepare CSV data
      const csvData = attempts.map(attempt => ({
        'Student ID': attempt.student?.student_reg || attempt.student_id || 'N/A',
        'Student Name': attempt.student?.full_name || attempt.student_name || 'Unknown',
        'Score': attempt.score !== null && attempt.score !== undefined ? attempt.score : 'N/A',
        'Status': attempt.status === 'completed' ? 'Completed' : 'In Progress',
        'Time Spent': formatTimeForCSV(attempt.time_spent),
        'Start Time': formatDateForCSV(attempt.start_time),
        'End Time': formatDateForCSV(attempt.end_time),
        'Percentage': attempt.score && quiz?.total_score 
          ? `${((attempt.score / quiz.total_score) * 100).toFixed(2)}%` 
          : 'N/A'
      }));

      // Convert to CSV string
      const headers = Object.keys(csvData[0]);
      const csvRows = [
        headers.join(','),
        ...csvData.map(row => 
          headers.map(header => {
            const value = row[header];
            // Handle values that contain commas or quotes
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          }).join(',')
        )
      ];
      
      const csvString = csvRows.join('\n');
      
      // Create download link
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${quiz?.title || 'quiz'}_attempts_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  // Helper function to format time for CSV
  const formatTimeForCSV = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Helper function to format date for CSV
  const formatDateForCSV = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Helper function to get option text by label
  const getOptionText = (question, answerLabel) => {
    if (!question || !question.options || !answerLabel) return answerLabel;
    
    const option = question.options.find(opt => opt.label === answerLabel);
    return option ? option.text : answerLabel;
  };

  // Helper function to get all options for a question
  const getAllOptions = (question) => {
    if (!question || !question.options) return [];
    return question.options;
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '—';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return 'text-gray-500';
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompletedCount = () => {
    if (!Array.isArray(attempts)) return 0;
    return attempts.filter(a => a.status === 'completed').length;
  };

  const getInProgressCount = () => {
    if (!Array.isArray(attempts)) return 0;
    return attempts.filter(a => a.status === 'in_progress').length;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const attemptsArray = Array.isArray(attempts) ? attempts : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/admin/quizzes/${quizId}`} className="text-blue-600 hover:text-blue-800">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quiz Attempts</h1>
            <p className="text-gray-600">{quiz?.title || 'Loading...'}</p>
          </div>
        </div>
        
        {/* Export Button */}
        {attemptsArray.length > 0 && (
          <button
            onClick={exportToCSV}
            disabled={exportLoading}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Export CSV</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">Total Attempts</p>
          <p className="text-2xl font-bold text-gray-900">{attemptsArray.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">Completed</p>
          <p className="text-2xl font-bold text-green-600">{getCompletedCount()}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
          <p className="text-sm text-gray-600">In Progress</p>
          <p className="text-2xl font-bold text-yellow-600">{getInProgressCount()}</p>
        </div>
      </div>

      {/* Attempts Table */}
      {attemptsArray.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center border border-gray-100">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"/>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No attempts yet</h3>
          <p className="text-gray-500">Students haven't taken this quiz yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-100">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time Spent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attemptsArray.map((attempt, idx) => (
                  <tr key={attempt.id || idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {attempt.student?.full_name || attempt.student_name || 'Unknown'}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {attempt.student?.student_reg || '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(attempt.start_time)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        attempt.status === 'completed' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {attempt.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm font-medium ${getScoreColor(attempt.score)}`}>
                        {attempt.score !== null && attempt.score !== undefined ? `${attempt.score}` : '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatTime(attempt.time_spent)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => {
                          setSelectedAttempt(attempt);
                          setShowModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View Answers
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Answers Modal */}
      {showModal && selectedAttempt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-white font-bold text-lg">Student Answers</h3>
                <p className="text-blue-100 text-sm">
                  {selectedAttempt.student?.full_name || selectedAttempt.student_name} • Score: {selectedAttempt.score || 0}
                </p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-white hover:text-gray-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {selectedAttempt.answers && selectedAttempt.answers.length > 0 ? (
                <div className="space-y-4">
                  {selectedAttempt.answers.map((ans, idx) => {
                    // Find the question
                    const question = quiz?.questions?.find(q => q.id === ans.question_id);
                    
                    // Get the full option text for student's answer
                    const studentAnswerText = getOptionText(question, ans.answer_text);
                    
                    // Get all options for this question
                    const allOptions = getAllOptions(question);
                    
                    // Get the correct answer text
                    const correctAnswerText = question?.correct_answer 
                      ? getOptionText(question, question.correct_answer)
                      : question?.correct_answer;
                    
                    return (
                      <div key={ans.id || idx} className={`border-l-4 p-4 rounded-r-lg ${
                        ans.is_correct ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-gray-900">Question {idx + 1}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            ans.is_correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                          }`}>
                            {ans.is_correct ? 'Correct' : 'Incorrect'}
                          </span>
                        </div>
                        
                        {/* Question Text */}
                        <div 
                          className="text-gray-700 mb-3 prose max-w-none"
                          dangerouslySetInnerHTML={{ __html: question?.question_text || 'Question not found' }} 
                        />
                        
                        {/* Student's Answer with full text */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Student's Answer:</p>
                          <div className={`p-3 rounded-lg border ${
                            ans.is_correct 
                              ? 'bg-green-100 border-green-300' 
                              : 'bg-red-100 border-red-300'
                          }`}>
                            <div className="flex items-start gap-2">
                              <span className="font-bold text-gray-700 min-w-8">
                                {ans.answer_text}:
                              </span>
                              <span 
                                className="text-gray-800 flex-1"
                                dangerouslySetInnerHTML={{ __html: studentAnswerText || 'No answer provided' }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Correct Answer with full text */}
                        <div className="mb-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Correct Answer:</p>
                          <div className="p-3 bg-green-100 rounded-lg border border-green-300">
                            <div className="flex items-start gap-2">
                              <span className="font-bold text-green-700 min-w-8">
                                {question?.correct_answer}:
                              </span>
                              <span 
                                className="text-green-800 flex-1"
                                dangerouslySetInnerHTML={{ __html: correctAnswerText || 'N/A' }}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* All Options Section */}
                        {allOptions.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-500 mb-2">All Options:</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {allOptions.map((opt, optIdx) => {
                                const isStudentAnswer = opt.label === ans.answer_text;
                                const isCorrectAnswer = opt.label === question?.correct_answer;
                                
                                let bgColor = '';
                                let borderColor = 'border-gray-200';
                                
                                if (isStudentAnswer && isCorrectAnswer) {
                                  bgColor = 'bg-purple-100';
                                  borderColor = 'border-purple-400';
                                } else if (isStudentAnswer) {
                                  bgColor = 'bg-blue-100';
                                  borderColor = 'border-blue-400';
                                } else if (isCorrectAnswer) {
                                  bgColor = 'bg-green-100';
                                  borderColor = 'border-green-400';
                                } else {
                                  bgColor = 'bg-gray-50';
                                }
                                
                                return (
                                  <div 
                                    key={optIdx} 
                                    className={`p-2 rounded-lg border ${borderColor} ${bgColor}`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <span className="font-bold text-gray-700 min-w-6">
                                        {opt.label}:
                                      </span>
                                      <span 
                                        className="text-gray-700 text-sm flex-1"
                                        dangerouslySetInnerHTML={{ __html: opt.text }}
                                      />
                                      {isStudentAnswer && (
                                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                                          Student
                                        </span>
                                      )}
                                      {isCorrectAnswer && !isStudentAnswer && (
                                        <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">
                                          Correct
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Explanation */}
                        {question?.explanation && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="font-medium text-blue-800 mb-1">Explanation:</p>
                            <p className="text-blue-700 text-sm">{question.explanation}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No answers recorded for this attempt.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuizAttempts;